import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";

import FrequencySelect from "~/components/common/FrequencySelect";
import { useTeam } from "~/contexts/TeamProvider";

import { apiClient } from "../../../api/ApiClient";
import FormLabel from "../../login/formLabel/FormLabel";
import Select from "../../mantine/select/Select";
import Switch from "../../switch/Switch";
import styles from "./KeywordAttributes.module.scss";

interface KeywordAttributesFormData {
  refresh_frequency_override: number | null;
  location_id?: number;
  region: string;
  language: string;
  isTrackLocal: boolean;
}

type KeywordAttributesProps = {
  createKeywords?: (data: {
    refresh_frequency_override: number | null;
    location_id?: number;
    country: string;
  }) => void;
};

const KeywordAttributes = ({ createKeywords }: KeywordAttributesProps) => {
  const { team } = useTeam();
  const [locationOptions, setLocationOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const { control, handleSubmit, watch, setValue } =
    useForm<KeywordAttributesFormData>({
      defaultValues: {
        refresh_frequency_override: null,
        location_id: 0,
        region: "google.com",
        language: "en",
        isTrackLocal: false,
      },
    });
  const { mutate: getLocations } = useMutation({
    mutationFn: async (data: string) => {
      const response = await apiClient.locations.searchList({
        fulltext: data,
      });

      const locationOptions =
        response.data.locations?.map((location) => ({
          value: String(location.id),
          label: location.name || "",
        })) || [];
      setLocationOptions(locationOptions);

      return locationOptions;
    },
  });
  const { data: languages } = useQuery({
    queryKey: ["languagesList"],
    queryFn: async () => {
      const response = await apiClient.sites.languagesList();

      return response?.data.languages;
    },
    staleTime: 1000 * 60 * 30,
  });
  const { data: regions } = useQuery({
    queryKey: ["regionsList"],
    queryFn: async () => {
      const response = await apiClient.sites.regionsList();

      return response?.data.regions;
    },
    staleTime: 1000 * 60 * 30,
  });

  const onSubmit = (data: KeywordAttributesFormData) => {
    const payload = {
      refresh_frequency_override: data.refresh_frequency_override,
      ...(data.location_id && { location_id: Number(data.location_id) }),
      country: data.region,
      language: data.language,
    };
    createKeywords?.(payload);
  };

  const regionOptions = regions
    ? Object.entries(regions).map(([key, value]) => ({
        value: key,
        label: `${key} (${value.name})`,
      }))
    : [];
  const languageOptions = languages
    ? Object.entries(languages).map(([key, value]) => ({
        value: key,
        label: `${value} (${key})`,
      }))
    : [];

  return (
    // @ts-expect-error TS2339 non-existing className
    <div className={styles.attributesContainer}>
      <form onSubmit={handleSubmit(onSubmit)} id="keywordSuggestions">
        <div className={styles.formGrid}>
          <Controller
            name="region"
            control={control}
            render={({ field }) => (
              <Select
                label={<FormLabel label="Country" />}
                data={regionOptions}
                searchable
                {...field}
                value={field.value ? String(field.value) : ""}
              />
            )}
          />
          <Controller
            name="language"
            control={control}
            render={({ field }) => (
              <Select
                label={<FormLabel label="Language" />}
                data={languageOptions}
                searchable
                {...field}
                value={field.value ? String(field.value) : ""}
              />
            )}
          />
          <Controller
            name="refresh_frequency_override"
            control={control}
            render={({ field }) => (
              <FrequencySelect
                label="Refresh Frequency"
                defaultSeconds={team?.refresh_frequency}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <div className={styles.formGridSwitch}>
            <Switch
              checked={watch("isTrackLocal")}
              onChange={(e) =>
                setValue("isTrackLocal", e.currentTarget.checked, {
                  shouldDirty: true,
                })
              }
            />
            <FormLabel label="Track Local Results" />
          </div>

          {watch("isTrackLocal") && (
            <Controller
              name="location_id"
              control={control}
              render={({ field }) => (
                <Select
                  label={<FormLabel label="Location" />}
                  data={locationOptions}
                  searchable
                  clearable
                  {...field}
                  onSearchChange={(query) => {
                    getLocations(query);
                  }}
                  value={field.value ? String(field.value) : ""}
                  placeholder="Enter a location or zip code"
                  comboboxProps={{ position: "bottom" }}
                />
              )}
            />
          )}
        </div>
      </form>
    </div>
  );
};

export default KeywordAttributes;
